import type { Messages } from '@ah/i18n';

/** English translation (ADR-015). Any key missing here falls back to el.ts. */
export const en: Messages = {
  auth: {
    login: {
      title: 'Therapist sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      missingFields: 'Please enter your email and password.',
      twoFactorUnsupported: 'Two-factor authentication is not supported for this account type. Please contact support.',
      wrongPortal: 'This portal is for therapists only. Please sign in with a therapist account.',
      failed: 'Sign in failed. Please try again.',
      noAccount: 'New to Anonymous Help as a therapist?',
      registerLink: 'Register',
    },
    register: {
      title: 'Register as a therapist',
      intro: "Your account is reviewed before you can be matched with clients — you can sign in and explore the portal in the meantime.",
      email: 'Email',
      password: 'Password (min 10 chars, mixed case + number)',
      displayName: 'Display name',
      displayNamePlaceholder: 'e.g. Dr. Maria Papadopoulou',
      licenseNumber: 'License number',
      licenseCountry: 'License country (2-letter code)',
      specialties: 'Specialties (comma-separated)',
      specialtiesPlaceholder: 'e.g. anxiety, trauma, relationships',
      specialtiesError: 'List at least one specialty, separated by commas.',
      failed: 'Registration failed. Please try again.',
      legalNoticePrefix: 'By registering you accept the',
      legalNoticeJoiner: 'and the',
      termsLink: 'Terms of Use',
      privacyLink: 'Privacy Policy',
      alreadyRegistered: 'Already registered?',
      loginLink: 'Sign in',
      submit: 'Create therapist account',
      submitting: 'Creating your account…',
    },
  },
  dashboard: {
    title: 'Therapist Portal',
    myProfile: 'My profile',
    payouts: 'Payouts',
    settings: 'Settings',
    signOut: 'Sign out',
    pendingReview: "Your account is pending review — you can explore the portal now, but you won't be matched with clients until an admin approves it.",
    suspended: 'Your account is suspended. Contact an administrator for details.',
    rejected: 'Your application was not approved. Contact an administrator for details.',
    loading: 'Loading…',
    loadError: 'Could not load your bookings.',
    yourBookings: 'Your bookings',
    noBookings: 'No bookings yet.',
    working: 'Working…',
    accept: 'Accept',
    decline: 'Decline',
    declinePrompt: 'Reason for declining (optional):',
    acceptError: 'Could not accept this booking. Please try again.',
    declineError: 'Could not decline this booking. Please try again.',
    awaitingPayment: 'Awaiting client payment.',
    openCall: 'Open call',
  },
  payouts: {
    title: 'Payouts',
    backDashboard: '← Dashboard',
    connectStatusTitle: 'Stripe Connect status',
    onboarded: "✅ Payments are set up — you'll receive 90% of each booking, paid out weekly.",
    notFullySetUp: "Your Stripe account isn't fully set up yet — finish onboarding to start receiving payouts.",
    notConnected: "You haven't connected a payout account yet.",
    completeOnboarding: 'Complete Stripe onboarding',
    redirecting: 'Redirecting…',
    onboardingError: 'Could not start onboarding.',
    loadError: 'Could not load payment info.',
    payoutHistory: 'Payout history',
    noPayouts: 'No payouts yet.',
    bookingsSuffix: 'booking(s)',
    net: 'net',
  },
  settings: {
    title: 'Account settings',
    backDashboard: '← Dashboard',
    language: 'Language',
    deleteAccount: 'Delete account',
    scheduledFor: 'Your account is scheduled for deletion on {{date}}. You can cancel any time before then.',
    cancelling: 'Cancelling…',
    cancelRequest: 'Cancel deletion request',
    graceNotice: "This starts a {{days}}-day grace period during which you can still cancel. After that, your public profile, messages, and login are permanently anonymized. Please make sure you have no upcoming bookings and have contacted support about any pending payout first — booking and invoice records are kept as required by tax law, disconnected from your identity. Enter your password to confirm.",
    password: 'Password',
    submitting: 'Submitting…',
    confirmDeletion: 'Confirm deletion',
    cancel: 'Cancel',
    deleteMyAccount: 'Delete my account',
    deletionRequestError: 'Could not submit the deletion request.',
    deletionCancelError: 'Could not cancel the deletion request.',
  },
  profile: {
    title: 'My profile',
    backDashboard: '← Dashboard',
    intro: "This is what clients see on the landing page: photo, headline, bio, intro video, specializations, and the case types you take on.",
    loading: 'Loading…',
    loadError: 'Could not load your profile.',
    photo: 'Profile photo',
    photoUpdated: 'Photo updated.',
    photoUploadFailed: 'Upload failed.',
    tabEl: 'Greek',
    tabEn: 'English',
    requiredBadge: 'required',
    optionalBadge: 'optional',
    completeBadge: '✓ complete',
    incompleteBadge: 'incomplete',
    headlineLabel: 'Headline (e.g. "Clinical Psychologist, 12 years experience")',
    bioLabel: 'Bio / description',
    introVideoLabel: 'Intro video (YouTube/Vimeo link — optional, shared across both languages)',
    categoriesLegend: 'Categories (specializations — pick from the list, shared across both languages)',
    caseTagsLabel: 'Case types I take on (comma-separated tags)',
    caseTagsPlaceholderEl: 'e.g. panic attacks, grief, burnout',
    caseTagsPlaceholderEn: 'e.g. panic attacks, grief, burnout',
    spokenLanguagesLegend: 'Session languages (which languages you can hold the session in)',
    save: 'Save profile',
    saving: 'Saving…',
    saveSuccess: "Profile saved — it's live on the landing page immediately (once you're approved).",
    saveFailed: 'Save failed.',
  },
  call: {
    connecting: 'Connecting…',
    waitingForOther: 'Waiting for the client to join…',
    ending: 'Ending call…',
    ended: 'Call ended — {{duration}}',
    anonymousNotice: 'Anonymous call — your voice is disguised, camera is off.',
    mute: 'Mute',
    unmute: 'Unmute',
    endCall: 'End call',
    done: 'Done',
    back: 'Back',
    genericError: 'Something went wrong.',
    needSignIn: 'You need to sign in to join this call.',
    joinError: 'Could not join the call.',
  },
};
